import Grow from "@mui/material/Grow";
import {
  DeFlagIcon,
  DevFlagIcon,
  UkFlagIcon,
} from "ds_legacy/components/FlagIcon";
import { RSFAB } from "ds_legacy/components/RSButton";
import { dp, sizing } from "ds_legacy/materials/metrics";
import { useScreenReaderStyles } from "ds_legacy/materials/screenreaders";
import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGetLanguage, useLanguageChange } from "reduxentities/hooks";

const TIMEOUT = 500;

const LANGUAGE_FLAGS: {
  [key: string]: React.ComponentType<{ active?: boolean }>;
} = { de: DeFlagIcon, dev: DevFlagIcon, en: UkFlagIcon };

function LanguageFabs({
  checked,
  flags,
  onFlagChange,
  toggleCheck,
}: {
  checked: boolean;
  flags: {
    [key: string]: React.ComponentType<{ active?: boolean }>;
  };
  onFlagChange: AnyFunction;
  toggleCheck: AnyFunction;
}) {
  return (
    <>
      {Object.keys(flags).map((flag, index, flagsArray) => {
        const Flag = flags[flag];
        const inTransitionDelay = `${(flagsArray.length - index) * 0.1}s`;
        const outTransitionDelay = `${index * 0.1}s`;
        return (
          <Grow
            key={flag}
            in={checked}
            style={{
              transitionDelay: checked ? inTransitionDelay : outTransitionDelay,
            }}
            {...(checked ? { timeout: TIMEOUT } : {})}
          >
            <div style={{ marginBottom: sizing(2) }}>
              <RSFAB
                id={`language-${flag}`}
                inverted
                onClick={() => {
                  onFlagChange(flag);
                  toggleCheck();
                }}
              >
                <Flag active />
              </RSFAB>
            </div>
          </Grow>
        );
      })}
    </>
  );
}

const defaultFabContainerStyles: CSSProperties = {
  bottom: dp(100),
  display: "flex",
  flex: "0 0 auto",
  flexDirection: "column",
  left: dp(30),
  position: "fixed",
  zIndex: 100,
};

export function FlagFab({
  containerStyleOverride,
  flags,
  flagState,
  onFlagChange,
}: {
  containerStyleOverride?: CSSProperties;
  flagState: string | undefined;
  flags: {
    [key: string]: React.ComponentType<{ active?: boolean }>;
  };
  onFlagChange: AnyFunction;
}) {
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const toggleCheck = useCallback(() => setChecked(!checked), [checked]);
  const Flag = flags[flagState || ""];
  const [renderPicker, setRenderPicker] = useState(checked);
  const { srOnly } = useScreenReaderStyles();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (checked) {
      setRenderPicker(true);
    } else {
      timeout = setTimeout(() => {
        setRenderPicker(false);
      }, TIMEOUT);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [checked]);

  useEffect(() => {
    setChecked(false);
  }, [location.pathname]);

  return (
    <div
      data-testid="language-switcher"
      style={
        containerStyleOverride
          ? containerStyleOverride
          : defaultFabContainerStyles
      }
    >
      {renderPicker && (
        <LanguageFabs
          checked={checked}
          flags={flags}
          toggleCheck={toggleCheck}
          onFlagChange={onFlagChange}
        />
      )}
      <RSFAB
        id="language-switcher"
        inverted
        sx={{ displayPrint: "none" }}
        onClick={toggleCheck}
      >
        <Flag />
        <span className={srOnly}>{flagState}</span>
      </RSFAB>
    </div>
  );
}

export default function LanguageFab() {
  const onLanguageChange = useLanguageChange();
  const language = useGetLanguage();

  return (
    <FlagFab
      flags={LANGUAGE_FLAGS}
      flagState={language}
      onFlagChange={onLanguageChange}
    />
  );
}
